import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import styled from "styled-components";

import {
  LOCALES,
  LOCALE_COOKIE,
  Locale,
  createTranslator,
  isLocale,
  isTranslatedRoute,
  normalizePathname,
  useLocaleSwitcher,
} from "lib/i18n";

const DISMISS_KEY = "lawfully.languageBanner.dismissed";

/**
 * Offers a language swap to visitors whose browser prefers the other language.
 *
 * Detection runs on the client, not the server. Most marketing pages have no
 * data fetching at all and are static HTML from build time, so there is no
 * request context to read Accept-Language from, and converting them to SSR to
 * get one would cost exactly the performance the SEO work is protecting.
 * Nothing here is crawler-visible, so this costs no SEO.
 *
 * Dismissal is remembered in localStorage rather than a cookie on purpose: a new
 * cookie would have to be reasoned about in the CloudFront cache key, and this
 * preference never needs to reach the server.
 */
const LanguageBanner: React.FC = () => {
  const router = useRouter();
  const { locale, switchTo } = useLocaleSwitcher();
  const [suggested, setSuggested] = useState<Locale | null>(null);

  // Deliberately NOT the current locale. The banner speaks to someone whose
  // browser is set to the other language, so it has to be written in that
  // language -- offering "Switch to English" in English to a Spanish speaker
  // reading an English page tells them nothing they wanted to know. It also
  // keeps the copy consistent with the `lang` attribute below.
  const t = useMemo(
    () => createTranslator(suggested ?? locale, "common"),
    [suggested, locale],
  );

  // Recomputed on every locale or route change, and it must always write --
  // including null. Bailing out early without clearing would leave a stale
  // suggestion on screen after the user acts on it: switching language sets the
  // cookie and re-runs this, and the banner would otherwise keep offering
  // Spanish to someone already reading Spanish.
  useEffect(() => {
    setSuggested(detectSuggestion(router.asPath, locale));
  }, [locale, router.asPath]);

  if (!suggested) return null;

  const handleDismiss = () => {
    try {
      window.localStorage?.setItem(DISMISS_KEY, "1");
    } catch {
      // Private browsing can throw on write; dismissing for this page is enough.
    }
    setSuggested(null);
  };

  return (
    // Fixed to the bottom so appearing after hydration shifts nothing above it.
    <Bar
      role="region"
      aria-label={t("languageSwitcher.label")}
      lang={suggested}
    >
      <Message>{t("languageBanner.message")}</Message>
      <Action type="button" onClick={() => switchTo(suggested)}>
        {t("languageBanner.action")}
      </Action>
      <Dismiss
        type="button"
        onClick={handleDismiss}
        aria-label={t("languageBanner.dismiss")}
      >
        ×
      </Dismiss>
    </Bar>
  );
};

/**
 * The locale worth offering on this page, or null to show nothing.
 */
function detectSuggestion(asPath: string, locale: Locale): Locale | null {
  // Nothing to offer unless this exact page exists in the other language.
  if (!isTranslatedRoute(normalizePathname(asPath))) return null;

  // An explicit choice, in either direction, ends the guessing.
  if (isLocale(parseCookies(null)[LOCALE_COOKIE])) return null;

  if (readDismissed()) return null;

  const preferred = detectPreferredLocale();
  return preferred && preferred !== locale ? preferred : null;
}

function readDismissed(): boolean {
  try {
    return window.localStorage?.getItem(DISMISS_KEY) === "1";
  } catch {
    // Private browsing can throw on read; treat it as not dismissed.
    return false;
  }
}

/**
 * First browser preference we actually support. `languages` is ordered by the
 * user's own ranking, so it beats the single `language` value.
 */
function detectPreferredLocale(): Locale | null {
  const candidates =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];

  for (const candidate of candidates) {
    // "es-419" and "es-MX" are both Spanish for our purposes.
    const base = candidate?.split("-")[0]?.toLowerCase();
    const match = LOCALES.find((supported) => supported === base);
    if (match) return match;
  }

  return null;
}

const Bar = styled.div`
  position: fixed;
  z-index: 1000;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: #1a1a2e;
  color: #ffffff;
  font-size: 14px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
`;

const Message = styled.span`
  font-weight: 500;
`;

const Action = styled.button`
  padding: 6px 14px;
  border: 0;
  border-radius: 6px;
  background-color: #195dee;
  color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background-color: #1450cd;
  }
`;

const Dismiss = styled.button`
  padding: 0 4px;
  border: 0;
  background: none;
  color: inherit;
  font-size: 20px;
  line-height: 1;
  opacity: 0.7;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

export default LanguageBanner;
